import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
    Plus, Search, Camera, Edit, Trash2, MapPin, Wifi, 
    Network, Monitor, X, Check, Eye, EyeOff, Upload, Download, FileSpreadsheet
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function Cameras() {
    const { isAdmin } = useAuth();
    const [cameras, setCameras] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [editingCamera, setEditingCamera] = useState(null);
    const [showPasswords, setShowPasswords] = useState({});
    const [importData, setImportData] = useState('');
    const fileInputRef = useRef(null);

    const [formData, setFormData] = useState({
        name: '',
        location: '',
        ip_address: '',
        mac_address: '',
        brand: '',
        model: '',
        resolution: '',
        notes: '',
    });

    useEffect(() => {
        loadCameras();
    }, []);

    const loadCameras = async (searchTerm = '') => {
        try {
            const token = localStorage.getItem('token');
            const url = searchTerm 
                ? `${API_URL}/api/cameras?search=${encodeURIComponent(searchTerm)}`
                : `${API_URL}/api/cameras`;
            
            const res = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setCameras(data);
            }
        } catch (error) {
            console.error('Load error:', error);
            toast.error('Kameralar yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value) => {
        setSearch(value);
        loadCameras(value);
    };

    const openCreateDialog = () => {
        setEditingCamera(null);
        setFormData({
            name: '',
            location: '',
            ip_address: '',
            mac_address: '',
            brand: '',
            model: '',
            resolution: '',
            notes: '',
        });
        setDialogOpen(true);
    };

    const openEditDialog = (camera) => {
        setEditingCamera(camera);
        setFormData({
            name: camera.name || '',
            location: camera.location || '',
            ip_address: camera.ip_address || '',
            mac_address: camera.mac_address || '',
            brand: camera.brand || '',
            model: camera.model || '',
            resolution: camera.resolution || '',
            notes: camera.notes || '',
        });
        setDialogOpen(true);
    };

    const handleSubmit = async () => {
        if (!formData.name || !formData.location) {
            toast.error('Kamera adı ve konum zorunludur');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const url = editingCamera 
                ? `${API_URL}/api/cameras/${editingCamera.id}`
                : `${API_URL}/api/cameras`;
            
            const res = await fetch(url, {
                method: editingCamera ? 'PUT' : 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (res.ok) {
                toast.success(editingCamera ? 'Kamera güncellendi' : 'Kamera eklendi');
                setDialogOpen(false);
                loadCameras(search);
            } else {
                toast.error('İşlem başarısız');
            }
        } catch (error) {
            console.error('Submit error:', error);
            toast.error('Bir hata oluştu');
        }
    };

    const handleDelete = async (camera) => {
        if (!window.confirm(`"${camera.name}" kamerasını silmek istediğinize emin misiniz?`)) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/cameras/${camera.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` },
            });

            if (res.ok) {
                toast.success('Kamera silindi');
                loadCameras(search);
            } else {
                toast.error('Silme işlemi başarısız');
            }
        } catch (error) {
            console.error('Delete error:', error);
            toast.error('Bir hata oluştu');
        }
    };

    const handleBulkImport = async () => {
        if (!importData.trim()) {
            toast.error('Lütfen veri girin');
            return;
        }

        try {
            // Parse CSV/TSV data
            const lines = importData.trim().split('\n');
            const items = [];
            
            for (let i = 0; i < lines.length; i++) {
                const line = lines[i].trim();
                if (!line) continue;
                
                // Split by tab or semicolon
                const parts = line.includes('\t') ? line.split('\t') : line.split(';');
                
                if (parts.length >= 2) {
                    items.push({
                        name: parts[0]?.trim() || '',
                        location: parts[1]?.trim() || '',
                        ip_address: parts[2]?.trim() || '',
                        mac_address: parts[3]?.trim() || '',
                        brand: parts[4]?.trim() || '',
                        model: parts[5]?.trim() || '',
                        resolution: parts[6]?.trim() || '',
                    });
                }
            }

            if (items.length === 0) {
                toast.error('Geçerli veri bulunamadı');
                return;
            }

            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/cameras/bulk-import`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(items),
            });

            if (res.ok) {
                const result = await res.json();
                toast.success(`${result.imported} kamera başarıyla eklendi`);
                if (result.errors?.length > 0) {
                    toast.warning(`${result.errors.length} satırda hata oluştu`);
                }
                setImportDialogOpen(false);
                setImportData('');
                loadCameras(search);
            } else {
                toast.error('Import işlemi başarısız');
            }
        } catch (error) {
            console.error('Import error:', error);
            toast.error('Bir hata oluştu');
        }
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            setImportData(event.target.result);
        };
        reader.readAsText(file);
    };

    if (loading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-12 w-full" />
                <div className="grid gap-4">
                    {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="cameras-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-slate-800">Kamera Yönetimi</h1>
                    <p className="text-slate-500 text-sm mt-1">
                        Toplam {cameras.length} kamera kayıtlı
                    </p>
                </div>
                {isAdmin && (
                    <div className="flex gap-2">
                        <Button 
                            variant="outline"
                            onClick={() => setImportDialogOpen(true)}
                            data-testid="import-cameras-btn"
                        >
                            <Upload className="w-4 h-4 mr-2" />
                            Toplu Ekle
                        </Button>
                        <Button 
                            onClick={openCreateDialog}
                            className="bg-orwion-green hover:bg-orwion-green-hover" 
                            data-testid="create-camera-btn"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Yeni Kamera
                        </Button>
                    </div>
                )}
            </div>

            {/* Search */}
            <Card>
                <CardContent className="p-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            placeholder="Kamera adı, konum, IP veya MAC ile ara..."
                            value={search}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="pl-10"
                            data-testid="search-input"
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Camera List */}
            <div className="overflow-x-auto">
                <table className="w-full border-collapse bg-white rounded-lg shadow">
                    <thead>
                        <tr className="bg-slate-50 border-b">
                            <th className="text-left p-4 font-medium text-slate-600">Kamera Adı</th>
                            <th className="text-left p-4 font-medium text-slate-600">Konum</th>
                            <th className="text-left p-4 font-medium text-slate-600">IP Adresi</th>
                            <th className="text-left p-4 font-medium text-slate-600">MAC Adresi</th>
                            <th className="text-left p-4 font-medium text-slate-600">Marka/Model</th>
                            <th className="text-left p-4 font-medium text-slate-600">Çözünürlük</th>
                            <th className="text-left p-4 font-medium text-slate-600">Durum</th>
                            <th className="text-right p-4 font-medium text-slate-600">İşlemler</th>
                        </tr>
                    </thead>
                    <tbody>
                        {cameras.length === 0 ? (
                            <tr>
                                <td colSpan="8" className="p-8 text-center text-slate-500">
                                    <Camera className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                    <p>Henüz kamera kaydı yok</p>
                                </td>
                            </tr>
                        ) : (
                            cameras.map((camera) => (
                                <tr key={camera.id} className="border-b hover:bg-slate-50">
                                    <td className="p-4">
                                        <div className="flex items-center gap-2">
                                            <Camera className="w-4 h-4 text-slate-400" />
                                            <span className="font-medium">{camera.name}</span>
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <div className="flex items-center gap-1 text-slate-600">
                                            <MapPin className="w-3 h-3" />
                                            {camera.location}
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <code className="bg-slate-100 px-2 py-1 rounded text-sm">
                                            {camera.ip_address || '-'}
                                        </code>
                                    </td>
                                    <td className="p-4">
                                        <code className="bg-slate-100 px-2 py-1 rounded text-xs">
                                            {camera.mac_address || '-'}
                                        </code>
                                    </td>
                                    <td className="p-4 text-sm text-slate-600">
                                        {camera.brand && camera.model 
                                            ? `${camera.brand} ${camera.model}` 
                                            : camera.brand || camera.model || '-'}
                                    </td>
                                    <td className="p-4 text-sm text-slate-600">
                                        {camera.resolution || '-'}
                                    </td>
                                    <td className="p-4">
                                        <Badge className={camera.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                                            {camera.status === 'active' ? 'Aktif' : 'Pasif'}
                                        </Badge>
                                    </td>
                                    <td className="p-4 text-right">
                                        <div className="flex justify-end gap-2">
                                            <Button 
                                                variant="ghost" 
                                                size="icon" 
                                                onClick={() => openEditDialog(camera)}
                                                data-testid={`edit-camera-${camera.id}`}
                                            >
                                                <Edit className="w-4 h-4" />
                                            </Button>
                                            <Button 
                                                variant="ghost" 
                                                size="icon" 
                                                className="text-red-500 hover:text-red-600"
                                                onClick={() => handleDelete(camera)}
                                                data-testid={`delete-camera-${camera.id}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>
                            {editingCamera ? 'Kamera Düzenle' : 'Yeni Kamera Ekle'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="grid grid-cols-2 gap-4 py-4">
                        <div className="space-y-2">
                            <Label>Kamera Adı *</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                                placeholder="Örn: Lobi Kamera 1"
                                data-testid="camera-name-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Konum *</Label>
                            <Input
                                value={formData.location}
                                onChange={(e) => setFormData({...formData, location: e.target.value})}
                                placeholder="Örn: Ana Lobi"
                                data-testid="camera-location-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>IP Adresi</Label>
                            <Input
                                value={formData.ip_address}
                                onChange={(e) => setFormData({...formData, ip_address: e.target.value})}
                                placeholder="Örn: 192.168.1.100"
                                data-testid="camera-ip-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>MAC Adresi</Label>
                            <Input
                                value={formData.mac_address}
                                onChange={(e) => setFormData({...formData, mac_address: e.target.value})}
                                placeholder="Örn: AA:BB:CC:DD:EE:FF"
                                data-testid="camera-mac-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Marka</Label>
                            <Input
                                value={formData.brand}
                                onChange={(e) => setFormData({...formData, brand: e.target.value})}
                                placeholder="Örn: Hikvision"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Model</Label>
                            <Input
                                value={formData.model}
                                onChange={(e) => setFormData({...formData, model: e.target.value})}
                                placeholder="Örn: DS-2CD2143G0-I"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Çözünürlük</Label>
                            <Input
                                value={formData.resolution}
                                onChange={(e) => setFormData({...formData, resolution: e.target.value})}
                                placeholder="Örn: 4MP, 1080p"
                            />
                        </div>
                        <div className="col-span-2 space-y-2">
                            <Label>Notlar</Label>
                            <Textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                                placeholder="Ek notlar..."
                                rows={2}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            İptal
                        </Button>
                        <Button 
                            onClick={handleSubmit}
                            className="bg-orwion-green hover:bg-orwion-green-hover"
                            data-testid="save-camera-btn"
                        >
                            <Check className="w-4 h-4 mr-2" />
                            {editingCamera ? 'Güncelle' : 'Kaydet'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Import Dialog */}
            <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileSpreadsheet className="w-5 h-5" />
                            Toplu Kamera Ekle
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="bg-slate-50 p-4 rounded-lg text-sm">
                            <p className="font-medium mb-2">Format:</p>
                            <code className="text-xs bg-white p-2 block rounded border">
                                Kamera Adı; Konum; IP Adresi; MAC Adresi; Marka; Model; Çözünürlük
                            </code>
                            <p className="mt-2 text-slate-500">
                                Her satır bir kamera. Kolonları noktalı virgül (;) veya TAB ile ayırın.
                            </p>
                        </div>

                        <div>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileUpload}
                                accept=".txt,.csv,.tsv"
                                className="hidden"
                            />
                            <Button 
                                variant="outline" 
                                onClick={() => fileInputRef.current?.click()}
                                className="w-full"
                            >
                                <Upload className="w-4 h-4 mr-2" />
                                Dosya Yükle (.txt, .csv)
                            </Button>
                        </div>

                        <div className="space-y-2">
                            <Label>Veya veriyi yapıştırın:</Label>
                            <Textarea
                                value={importData}
                                onChange={(e) => setImportData(e.target.value)}
                                placeholder="Lobi Kamera 1; Ana Lobi; 192.168.1.100; AA:BB:CC:DD:EE:FF; Hikvision; DS-2CD2143G0-I; 4MP"
                                rows={10}
                                className="font-mono text-sm"
                            />
                        </div>

                        <div className="text-sm text-slate-500">
                            Örnek satır: <code className="bg-slate-100 px-1">Lobi Kamera 1; Ana Lobi; 192.168.1.100; AA:BB:CC:DD:EE:FF; Hikvision; DS-2CD2143G0-I; 4MP</code>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setImportDialogOpen(false)}>
                            İptal
                        </Button>
                        <Button 
                            onClick={handleBulkImport}
                            className="bg-orwion-green hover:bg-orwion-green-hover"
                        >
                            <Upload className="w-4 h-4 mr-2" />
                            {importData.split('\n').filter(l => l.trim()).length} Kamera Ekle
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default Cameras;
